import React, { useState, useEffect } from 'react';
import { Tag, Plus, Edit2, Trash2, Clock, DollarSign, X, Save, Loader2, Lock } from 'lucide-react';
import { auth } from '../config/firebase';
import { servicoService } from '../services/api';

const CatalogoServicos = ({ temAcesso }) => {
  const isPremium = temAcesso('descricao_servico');
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isServicoModalOpen, setIsServicoModalOpen] = useState(false);
  const [isSubmittingServico, setIsSubmittingServico] = useState(false);
  const [editingServicoId, setEditingServicoId] = useState(null);
  
  const [servicoForm, setServicoForm] = useState({
    nome_servico: '', descricao: '', preco: '', horas: 0, minutos: 30
  });

  const carregarDados = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const resServicos = await servicoService.listar(user.uid).catch(() => ({ data: [] }));
        setServicos(resServicos.data || []);
      }
    } catch (error) {
      console.error("Erro ao carregar serviços:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const abrirModalServico = (servico = null) => {
    if (servico) {
      setEditingServicoId(servico.id);
      const totalMinutos = servico.duracao_minutos || 30;
      setServicoForm({
        nome_servico: servico.nome_servico,
        descricao: servico.descricao || '',
        preco: servico.preco || '',
        horas: Math.floor(totalMinutos / 60),
        minutos: totalMinutos % 60
      });
    } else {
      setEditingServicoId(null);
      setServicoForm({ nome_servico: '', descricao: '', preco: '', horas: 0, minutos: 30 });
    }
    setIsServicoModalOpen(true);
  };

  const handleSalvarServico = async (e) => {
    e.preventDefault();
    const totalDuracaoEmMinutos = (parseInt(servicoForm.horas || 0) * 60) + parseInt(servicoForm.minutos || 0);

    if (totalDuracaoEmMinutos <= 0) return alert("A duração do serviço deve ser maior que zero.");

    setIsSubmittingServico(true);
    try {
      const user = auth.currentUser;
      const payload = {
        usuario_id: user.uid,
        nome_servico: servicoForm.nome_servico,
        // Só envia descrição se o plano liberar, senão força nulo para segurança
        descricao: isPremium ? servicoForm.descricao : null,
        preco: servicoForm.preco ? parseFloat(servicoForm.preco) : null,
        duracao_minutos: totalDuracaoEmMinutos
      };

      if (editingServicoId) {
        await servicoService.atualizar(editingServicoId, payload);
      } else {
        await servicoService.criar(payload);
      }
      
      await carregarDados();
      setIsServicoModalOpen(false);
    } catch (error) {
      alert("Erro ao salvar serviço.");
    } finally {
      setIsSubmittingServico(false);
    }
  };

  const handleDeletarServico = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este serviço?")) return;
    try {
      const user = auth.currentUser;
      await servicoService.desativar(id, user.uid);
      setServicos(servicos.filter(s => s.id !== id));
    } catch (error) {
      alert("Erro ao excluir serviço.");
    }
  };

  const formatarExibicaoDuracao = (minutosTotal) => {
    const h = Math.floor(minutosTotal / 60);
    const m = minutosTotal % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m} min`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="animate-spin text-[#1883FF]" size={24} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 border-b border-purple-50 pb-4">
        <div className="flex items-center gap-3">
          <Tag size={18} className="text-[#1883FF]" />
          <div>
            <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Catálogo de Serviços</h3>
            <p className="text-[10px] font-semibold text-slate-500 m-0 mt-1">Configure os serviços, preços e descrições para a IA oferecer aos clientes.</p>
          </div>
        </div>
        <button 
          onClick={() => abrirModalServico()}
          className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider cursor-pointer flex items-center gap-2 hover:opacity-90 shadow-sm shadow-[#6D28D9]/20 transition-all shrink-0"
        >
          <Plus size={14} /> Novo Serviço
        </button>
      </div>

      {servicos.length === 0 ? (
        <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-2xl p-10 flex flex-col items-center justify-center text-center">
          <Tag size={32} className="text-slate-300 mb-3" />
          <p className="text-sm font-bold text-slate-500 mb-1">Nenhum serviço cadastrado.</p>
          <p className="text-[10px] font-medium text-slate-400">Clique no botão acima para criar o seu primeiro serviço.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {servicos.map((servico) => (
            <div key={servico.id} className="bg-white border border-slate-100 p-5 rounded-2xl shadow-sm flex flex-col justify-between hover:border-purple-100 hover:shadow-md transition-all">
              <div>
                <div className="flex justify-between items-start gap-2 mb-2">
                  <h4 className="m-0 text-sm font-black text-slate-800 leading-tight">{servico.nome_servico}</h4>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => abrirModalServico(servico)} className="bg-transparent border-none p-1.5 text-slate-400 hover:text-[#1883FF] hover:bg-[#F5F3FF] rounded-lg cursor-pointer transition-colors"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeletarServico(servico.id)} className="bg-transparent border-none p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                
                {servico.descricao && isPremium && (
                  <p className="text-xs text-slate-500 font-medium line-clamp-3 mb-4 leading-relaxed">
                    {servico.descricao}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-2 pt-3 border-t border-slate-50">
                <span className="flex items-center gap-1 bg-[#F8F7FF] text-[#6D28D9] px-2 py-1 rounded-md text-[10px] font-bold">
                  <Clock size={12} /> {formatarExibicaoDuracao(servico.duracao_minutos)}
                </span>
                {servico.preco && (
                  <span className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-1 rounded-md text-[10px] font-bold">
                    <DollarSign size={12} /> R$ {parseFloat(servico.preco).toFixed(2).replace('.', ',')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isServicoModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[500px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0 flex items-center gap-2">
                <Tag size={18} className="text-[#1883FF]" />
                {editingServicoId ? "Editar Serviço" : "Novo Serviço"}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setIsServicoModalOpen(false)}>
                <X size={18}/>
              </button>
            </div>
            
            <form onSubmit={handleSalvarServico} className="flex flex-col gap-4 overflow-y-auto pr-1">
              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Nome do Serviço *</label>
                <input 
                  required type="text"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10" 
                  placeholder="Ex: Limpeza de Pele, Avaliação..." 
                  value={servicoForm.nome_servico} onChange={e => setServicoForm({...servicoForm, nome_servico: e.target.value})} 
                />
              </div>

              <div className="grid grid-cols-2 gap-4 text-left">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Duração *</label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-[#F8F7FF] border border-purple-50 rounded-xl focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-[#6D28D9]/10 transition-all overflow-hidden">
                      <input 
                        type="number" min="0" placeholder="0"
                        className="w-full bg-transparent py-3.5 pl-3 pr-1 text-sm font-bold text-slate-900 outline-none text-center" 
                        value={servicoForm.horas} onChange={e => setServicoForm({...servicoForm, horas: e.target.value})} 
                      />
                      <span className="text-[10px] font-black text-slate-400 pr-3 uppercase">h</span>
                    </div>
                    <div className="flex-1 flex items-center bg-[#F8F7FF] border border-purple-50 rounded-xl focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-[#6D28D9]/10 transition-all overflow-hidden">
                      <input 
                        type="number" min="0" max="59" placeholder="30"
                        className="w-full bg-transparent py-3.5 pl-3 pr-1 text-sm font-bold text-slate-900 outline-none text-center" 
                        value={servicoForm.minutos} onChange={e => setServicoForm({...servicoForm, minutos: e.target.value})} 
                      />
                      <span className="text-[10px] font-black text-slate-400 pr-3 uppercase">min</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Preço (R$)</label>
                  <input 
                    type="number" step="0.01" min="0"
                    className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10" 
                    placeholder="Ex: 150.00" 
                    value={servicoForm.preco} onChange={e => setServicoForm({...servicoForm, preco: e.target.value})} 
                  />
                </div>
              </div>
              
              {/* BLOQUEIO DO PLANO COMUM NA DESCRIÇÃO */}
              <div className="flex flex-col gap-1.5 text-left relative">
                <div className="flex items-center justify-between ml-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Descrição Detalhada</label>
                  {!isPremium && (
                    <span className="flex items-center gap-1 text-[8px] font-black uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md">
                      <Lock size={10} /> PRO
                    </span>
                  )}
                </div>
                
                <div className="relative w-full">
                  <textarea 
                    disabled={!isPremium}
                    className={`w-full py-3.5 px-4 rounded-xl text-sm font-semibold outline-none min-h-[100px] resize-none font-sans transition-all 
                      ${isPremium ? 'bg-[#F8F7FF] border border-purple-50 text-slate-900 focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10' : 'bg-slate-50 border border-slate-200 text-slate-400 cursor-not-allowed opacity-80'}`} 
                    placeholder={isPremium ? "Explique detalhes sobre este serviço que o robô pode precisar saber..." : "Disponível nos planos pagos."} 
                    value={servicoForm.descricao} onChange={e => setServicoForm({...servicoForm, descricao: e.target.value})} 
                  />
                  {!isPremium && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px] rounded-xl cursor-not-allowed">
                      <p className="bg-slate-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-lg">Faça Upgrade para habilitar</p>
                    </div>
                  )}
                </div>
              </div>

              <button 
                type="submit" disabled={isSubmittingServico} 
                className="w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none py-4 mt-4 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center shadow-lg shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
              >
                {isSubmittingServico ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isSubmittingServico ? " SALVANDO..." : " SALVAR SERVIÇO"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogoServicos;